<template lang="pug">
//- A duration field that takes what people actually type. The echo is the whole
//- point: a bare decimal is read as HOURS (1.25 = 1:15) while a whole number is
//- read as MINUTES (90 = 90), so `2` and `2.0` differ by an hour. Showing the
//- reading beside the field is what stops that being a silent mistake.
.duration-input
    el-input(
        ref="input"
        v-model="text"
        :size="size"
        :placeholder="placeholder || $t('e.g. 1:15, 1.25 or 90')"
        :class="{ 'is-unreadable': isUnreadable }"
        @input="emitParsed"
        @blur="onBlur"
    )
        //- Inside the field, not under it: the two dialogs that use this already
        //- carry a hint line, and a second one below would push Save off-screen.
        template(slot="suffix")
            span.duration-input__reading(v-if="isUnreadable") {{ $t('not a duration') }}
            span.duration-input__reading.is-quiet(v-else-if="minutes !== null") {{ reading }}
    //- The rules are not guessable — a bare 2 meaning two HOURS while a bare 90
    //- means ninety MINUTES is a product decision, not a notation. So the field
    //- states it, with the examples that show both sides of the seam.
    p.duration-input__help(v-if="isUnreadable") {{ $t('Not a duration. Try 1:15, 1.25 or 90.') }}
    p.duration-input__examples(v-else-if="showExamples") {{ $t('90 = 90 min · 2 = 2 hrs · 1:15 and 1.25 = 1:15 hrs') }}
</template>

<i18n>
{
    "en": {
        "{count} min": "{count} min",
        "{hours}:{minutes} hrs": "{hours}:{minutes} hrs"
    },
    "de": {
        "{count} min": "{count} Min.",
        "{hours}:{minutes} hrs": "{hours}:{minutes} Std.",
        "e.g. 1:15, 1.25 or 90": "z. B. 1:15, 1,25 oder 90",
        "not a duration": "keine Dauer",
        "Not a duration. Try 1:15, 1.25 or 90.": "Keine Dauer. Möglich sind 1:15, 1,25 oder 90.",
        "90 = 90 min · 2 = 2 hrs · 1:15 and 1.25 = 1:15 hrs": "90 = 90 Min. · 2 = 2 Std. · 1:15 und 1,25 = 1:15 Std."
    }
}
</i18n>

<script>
import { formatMinutesForInput, parseDurationToMinutes, splitDurationMinutes } from '@/helpers/duration';

export default {
    name: 'DurationInput',
    props: {
        // Minutes — the unit every caller and the API already speak.
        value: {
            type: Number,
            default: null,
        },
        size: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        // On by default: the reading is only obvious once the rules are known.
        showExamples: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            // What the agent typed, kept verbatim: rewriting it under them while
            // they type is how a "1:1" becomes "1:10" mid-keystroke.
            text: '',
        };
    },
    computed: {
        minutes() {
            return parseDurationToMinutes(this.text);
        },
        // Typed something, and it means nothing. An empty field is not an error.
        isUnreadable() {
            return String(this.text).trim() !== '' && this.minutes === null;
        },
        // Worded HERE, not in the helper: "Std." printed into an English admin is
        // exactly the mixed-language bug this had. Both keys carry an `en` entry
        // because a variable string without one renders the literal {count}.
        reading() {
            const { hours, minutes, total } = splitDurationMinutes(this.minutes);

            return total < 60
                ? this.$t('{count} min', { count: total })
                : this.$t('{hours}:{minutes} hrs', { hours, minutes: String(minutes).padStart(2, '0') });
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(minutes) {
                // Only when the model moved somewhere this text does not already
                // mean — otherwise typing "1:15" would be rewritten to "75".
                if (minutes === this.minutes) return;
                // Never String(minutes): below the small-integer seam that reads
                // back as HOURS, so a 3-minute session showed "3:00 hrs".
                this.text = minutes === null || minutes === undefined ? '' : formatMinutesForInput(minutes);
            },
        },
    },
    methods: {
        emitParsed() {
            // null on unreadable input, so a caller cannot save "abc" as a number.
            this.$emit('input', this.minutes);
        },
        /**
         * On leaving the field, replace what was typed with the reading. It is the
         * strongest confirmation available - the field itself now says 1:15 - and
         * it is what Toggl and Harvest both do. Only ever after a successful parse,
         * so unreadable text stays put for the agent to correct.
         */
        onBlur() {
            if (this.minutes === null) return;

            this.text = formatMinutesForInput(this.minutes);
        },
    },
};
</script>

<style lang="sass" scoped>
.duration-input
    ::v-deep .el-input__suffix
        display: flex
        align-items: center
        padding-right: 8px

    &__reading
        font-size: 12px
        color: $--color-danger
        white-space: nowrap

        &.is-quiet
            color: $color-grey-text

    &__help
        margin: 4px 0 0
        font-size: 12px
        line-height: 1.4
        color: $--color-danger

    &__examples
        margin: 4px 0 0
        font-size: 12px
        line-height: 1.4
        color: $color-grey-text

    ::v-deep .is-unreadable .el-input__inner
        border-color: $--color-danger
</style>
